// src/views/menage/MoisJourDetail.jsx — le dépliage d'UN JOUR dans le mois.
// RECALAGE MAQUETTE Dimitri 2026-08-19 : « deux écrans, une seule identité » —
// le détail du jour parle LE MÊME LANGAGE que le panneau de la frise (bandeau
// navy, cartes d'état colorées, blocs alternés à icônes pleines, code en
// carte navy, actions en pied).
//
// ⚠️ POURQUOI CE COMPOSANT PLUTÔT QUE `MenageDayDetail`. Celui-là empile une
// carte par ménage, chacune haute d'une dizaine de lignes : trois ménages
// remplissaient l'écran, et on perdait la grille de vue. Ici on veut d'abord
// une LISTE — voir la journée entière d'un coup — et n'ouvrir en détail que le
// ménage sur lequel on va agir. `MenageDayDetail` reste tel quel : il sert
// l'écran des agents, qui n'a pas ce besoin.
//
// ⚠️ LES SANS-AGENT EN TÊTE. Ce sont les seules lignes sur lesquelles une
// décision est possible tout de suite ; les mettre dans l'ordre chronologique
// les noierait au milieu de ce qui va bien.
//
// ⚠️ ON NE FIXE PAS D'HEURE ICI, et on ne déplace aucun créneau. Ça se fait au
// doigt dans la frise. Deux endroits pour le même geste, ce sont deux endroits à
// tenir cohérents — et un jour où ils ne le sont plus.

import { useState, useMemo } from "react"
import { DS } from "../../lib/ui"
import { firstNameFromEmail, libelleDepuisEmail, initialsFromEmail } from "./RunCard"
import {
  construireBlocs, agentsDisponibles, dureeDuMenage, formaterDuree,
  formaterHeureCourte, minutesDeHeure, arriveeAffichee,
} from "../../lib/menage-frise"
import {
  ChevronRight, ChevronDown, ClipboardList, AlertTriangle, Hourglass,
  MapPin, Key, User, UserRoundSearch, CalendarRange, CalendarDays, Clock,
  Check, X, Copy, Eye, RotateCw,
} from "lucide-react"
import { dansFenetreControle, raisonFenetrePassee } from "../../lib/controle-fenetre"
import { ETATS_PASTILLE } from "../../components/PastilleControle"

const C = {
  gris: '#3d5166', fonce: '#2e3d4d', ciel: '#87b8e6', cielPale: '#e4f0fb',
  cielTexte: '#1c2833', colonne: '#f4f9fd', bord: '#d5e3ee', contour: '#eef1f4',
  muet: '#98a2ad', texteDoux: '#5c6773',
  alerteFond: '#f7dcd1', alerteTexte: '#8f2f1d', alerteBord: '#f0d5c9',
  vertFond: '#f0f7f3', vertTexte: '#1f5a43',
  inter: '#7b6bb0',
  // Recalage 2026-08-19 — la charte du détail (mêmes hex que le panneau).
  navyFonce: '#233240', navy: '#3d5166', cielVif: '#5c8ab8',
  terra: '#b8412b', terraBord: '#e0b5a5', terraSombre: '#8f2f1d', peau: '#fdf9f7',
  vert: '#7fc4a3', vertBord: '#c9e0d4', vertPale: '#f7fbf9', vertSombre: '#0f3d2a',
}

// Le dégradé navy de la charte — le même que le bandeau du panneau.
const NAVY_DEGRADE = `linear-gradient(118deg, ${C.navyFonce} 0%, ${C.navy} 42%, ${C.cielVif} 100%)`

function libelleJourLong(iso) {
  const d = new Date(`${iso}T12:00:00`)
  if (Number.isNaN(d.getTime())) return iso
  const s = new Intl.DateTimeFormat('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' }).format(d)
  return s.charAt(0).toUpperCase() + s.slice(1)
}

/** « 11h → 17h » — la plage du logement, telle que le contrat la fixe. */
function libellePlage(info) {
  const co = minutesDeHeure(info?.checkout)
  const ci = minutesDeHeure(info?.checkin)
  if (co == null && ci == null) return null
  return `${co != null ? formaterHeureCourte(co) : '—'} → ${ci != null ? formaterHeureCourte(ci) : '—'}`
}

/** ISO → « 13h05 » Paris — l'heure du « validé à ». */
function heureValidation(iso) {
  if (!iso) return null
  try {
    const hm = new Intl.DateTimeFormat('fr-FR', {
      timeZone: 'Europe/Paris', hour: '2-digit', minute: '2-digit',
    }).format(new Date(iso)).replace(':', 'h')
    const net = hm.startsWith('0') ? hm.slice(1) : hm
    return net.endsWith('h00') ? net.slice(0, -2) : net
  } catch (_) { return null }
}

export default function MoisJourDetail({
  dayIso, runs, interventions, demandes, infoDe, logementNom, currentUser,
  assignOptions, regles, guestTimes, controlesParRun = {},
  controleursDefaut = {}, onControler = null, onOuvrirControle = null,
  onReassign, onOpenChecklist, onSignalIncident, onOuvrirDuree,
  onAllerFrise, onAllerDemande, onFermer,
  // MODE FILTRE (cartes d'action de la carte de titre, 2026-08-15) :
  // boutonsAction = chaque ligne porte « Assigner » / « Remplir » ;
  // piedSuivant = { dernier, onSuivant } — naviguer de jour concerné en jour
  // concerné sans repasser par la grille.
  boutonsAction = false, piedSuivant = null,
}) {
  const [ouvertId, setOuvertId] = useState(null)

  // ⚠️ LES SANS-AGENT EN TÊTE — voir l'en-tête de fichier.
  const lignes = useMemo(() => {
    const m = runs.map((r) => ({ type: 'menage', id: r.id, run: r, agent: r.assigned_provider_email }))
    const i = interventions.map((it) => ({ type: 'intervention', id: it.id, intervention: it, agent: it.assigned_provider_email }))
    return [...m, ...i].sort((a, b) => {
      const sa = a.agent ? 1 : 0
      const sb = b.agent ? 1 : 0
      if (sa !== sb) return sa - sb
      return String(a.id).localeCompare(String(b.id))
    })
  }, [runs, interventions])

  const sansAgent = lignes.filter((l) => !l.agent).length

  // Blocs du jour — servent à savoir QUI EST LIBRE au moment d'assigner.
  // Construits avec la règle unique, jamais recalculés à la main.
  const blocsDuJour = useMemo(() => {
    const logementsById = {}
    const horaires = {}
    for (const r of runs) {
      const info = infoDe(r.logement_id)
      logementsById[r.logement_id] = {
        id: r.logement_id, nom: logementNom(r.logement_id),
        menage_duree_min: info?.dureeMenage ?? null,
      }
      horaires[r.logement_id] = { checkout: info?.checkout, checkin: info?.checkin }
    }
    return construireBlocs({ runs, interventions, logementsById, horaires, heuresFixees: {} })
  }, [runs, interventions, infoDe, logementNom])

  // La pastille de date du bandeau : « Mar » / « 18 ».
  const d = new Date(`${dayIso}T12:00:00`)
  const jourCourt = Number.isNaN(d.getTime()) ? '' :
    new Intl.DateTimeFormat('fr-FR', { weekday: 'short' }).format(d).replace('.', '')
  const numJour = Number.isNaN(d.getTime()) ? '' : String(d.getDate())
  const nbMenages = lignes.filter((l) => l.type === 'menage').length
  const sousTitre = [
    `${nbMenages} ménage${nbMenages > 1 ? 's' : ''}`,
    demandes.length ? `${demandes.length} demande${demandes.length > 1 ? 's' : ''}` : null,
  ].filter(Boolean).join(' · ')

  return (
    <section data-testid={`mois-detail-${dayIso}`}
      style={{ marginTop: 16, border: `1px solid ${C.bord}`, borderRadius: 12, overflow: 'hidden' }}>

      {/* ── LE BANDEAU NAVY (maquette 2026-08-19) — même langage que le
          panneau : dégradé 118deg + disque translucide. ── */}
      <div style={{ position: 'relative', overflow: 'hidden', background: NAVY_DEGRADE,
        color: '#fff', padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 11 }}>
        <span aria-hidden="true" style={{ position: 'absolute', top: -30, right: 40,
          width: 88, height: 88, borderRadius: '50%', background: 'rgba(255,255,255,.05)' }} />
        <span aria-hidden="true" style={{ position: 'relative', width: 42, height: 42,
          borderRadius: 12, background: 'rgba(255,255,255,.16)', flexShrink: 0,
          display: 'inline-flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', lineHeight: 1.15 }}>
          <span style={{ fontSize: 9, fontWeight: 700, textTransform: 'capitalize',
            color: 'rgba(255,255,255,.75)' }}>{jourCourt}</span>
          <span style={{ fontSize: 17, fontWeight: 700 }}>{numJour}</span>
        </span>
        <span style={{ position: 'relative', flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: 15, fontWeight: 700 }}>
            {libelleJourLong(dayIso)}
          </span>
          <span style={{ display: 'block', fontSize: 11, color: 'rgba(255,255,255,.78)', marginTop: 1 }}>
            {sousTitre}
            {sansAgent > 0 && (
              <strong data-testid={`mois-detail-sansagent-${dayIso}`}
                style={{ color: '#f5c4b4' }}> · {sansAgent} sans agent</strong>
            )}
          </span>
        </span>
        {onFermer && (
          <button type="button" data-testid={`mois-fermer-${dayIso}`}
            onClick={onFermer} aria-label="Fermer" className="fbz-press fbz-hitbox44"
            style={{ position: 'relative', flexShrink: 0, width: 32, height: 32,
              borderRadius: 9, border: 'none', background: 'rgba(255,255,255,.14)',
              color: '#fff', cursor: 'pointer', display: 'inline-flex',
              alignItems: 'center', justifyContent: 'center' }}>
            <X size={16} strokeWidth={2.2} />
          </button>
        )}
      </div>

      <div style={{ background: '#fff' }}>
        {/* ── UNE DEMANDE D'HORAIRE ATTEND ── */}
        {demandes.map((dd) => (
          <button key={dd.id} type="button" data-testid={`mois-demande-${dd.id}`}
            onClick={() => onAllerDemande(dd, dayIso)}
            style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 9,
              padding: '10px 14px', background: C.colonne, border: 'none',
              borderBottom: `1px solid ${C.contour}`, cursor: 'pointer',
              fontFamily: DS.fontBody, textAlign: 'left' }}>
            {/* La MÊME pastille horloge que la case de la grille — cohérence
                grille ↔ détail (Dimitri 2026-08-14). */}
            <span aria-hidden="true" style={{ width: 20, height: 20, borderRadius: '50%',
              background: C.cielVif, flexShrink: 0, display: 'inline-flex',
              alignItems: 'center', justifyContent: 'center' }}>
              <Clock size={13} strokeWidth={2.4} color="#fff" />
            </span>
            <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: C.gris }}>
              Une demande d'horaire attend sur <strong>{logementNom(dd.logement_id)}</strong>
            </span>
            <ChevronRight size={15} strokeWidth={2.2} color={C.gris} style={{ flexShrink: 0 }} />
          </button>
        ))}

        {lignes.length === 0 && demandes.length === 0 && (
          <div style={{ padding: '22px 14px', textAlign: 'center', color: C.muet, fontSize: 12.5 }}>
            Rien de prévu ce jour-là.
          </div>
        )}

        {/* ── LES CARTES DU JOUR — l'état PAR LA COULEUR (maquette). ── */}
        <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {lignes.map((l) => (
            <div key={l.id}>
              <LigneJour ligne={l} ouvert={ouvertId === l.id}
                onBasculer={() => setOuvertId((c) => (c === l.id ? null : l.id))}
                infoDe={infoDe} logementNom={logementNom}
                rondControle={l.type === 'menage' && l.run.status === 'completed' ? (
                  <RondControleLigne run={l.run}
                    controle={controlesParRun[l.run.id] || null}
                    designe={controleursDefaut[l.run.logement_id] || null}
                    heureContrat={infoDe(l.run.logement_id)?.checkin || null}
                    onControler={onControler} onOuvrirControle={onOuvrirControle} />
                ) : null}
                // ⚠️ AUCUN DROIT NOUVEAU : « Assigner » OUVRE la carte (le
                // sélecteur « Assigner à » y vit, avec ses gardes) ; « Remplir »
                // ouvre la checklist par le MÊME chemin que le bouton de la carte.
                action={!boutonsAction || l.type !== 'menage' ? null
                  : !l.agent
                    ? { libelle: 'Assigner', testId: `mois-agir-assigner-${l.id}`,
                        onClic: () => setOuvertId(l.id) }
                    : l.run.status !== 'completed'
                      ? { libelle: 'Remplir', testId: `mois-agir-remplir-${l.id}`,
                          onClic: () => onOpenChecklist(l.run) }
                      : null} />
              {ouvertId === l.id && l.type === 'menage' && (
                <CarteMenage run={l.run} info={infoDe(l.run.logement_id)}
                  nom={logementNom(l.run.logement_id)} dayIso={dayIso}
                  controle={controlesParRun[l.run.id] || null}
                  blocsDuJour={blocsDuJour} assignOptions={assignOptions}
                  regle={regles?.[l.run.logement_id] || null}
                  guestTimes={guestTimes} currentUser={currentUser}
                  onReassign={onReassign} onOpenChecklist={onOpenChecklist}
                  onSignalIncident={onSignalIncident} onOuvrirDuree={onOuvrirDuree}
                  onAllerFrise={() => onAllerFrise(dayIso)} />
              )}
            </div>
          ))}
        </div>

        {/* ── LE PIED : le seul endroit où l'on AGIT sur la journée entière ── */}
        <div style={{ padding: '12px 14px', borderTop: `1px solid ${C.contour}` }}>
          <button type="button" data-testid={`mois-vers-frise-${dayIso}`}
            onClick={() => onAllerFrise(dayIso)} className="fbz-press"
            style={{ width: '100%', display: 'inline-flex', alignItems: 'center',
              justifyContent: 'center', gap: 7, background: NAVY_DEGRADE, color: '#fff',
              border: 'none', borderRadius: DS.radiusControl, padding: '11px 15px',
              fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: DS.fontBody }}>
            <CalendarRange size={15} strokeWidth={2} />
            Ouvrir le {new Date(`${dayIso}T12:00:00`).getDate()} {moisCourt(dayIso)} dans la frise
          </button>
          <div style={{ marginTop: 6, fontSize: 10, color: C.muet, textAlign: 'center' }}>
            pour assigner, déplacer les créneaux, ajouter une intervention
          </div>
          {/* MODE FILTRE : passer au jour concerné suivant sans repasser par la
              grille — sur le dernier, la mention grise non cliquable. */}
          {piedSuivant && (
            <div style={{ marginTop: 9, textAlign: 'center' }}>
              {piedSuivant.dernier ? (
                <span data-testid={`mois-jour-suivant-${dayIso}`}
                  style={{ fontSize: 11.5, color: C.muet, fontFamily: DS.fontBody }}>
                  dernier jour concerné
                </span>
              ) : (
                <button type="button" data-testid={`mois-jour-suivant-${dayIso}`}
                  onClick={piedSuivant.onSuivant} className="fbz-press fbz-cible-tactile"
                  style={{ background: 'none', border: 'none', cursor: 'pointer',
                    fontSize: 11.5, fontWeight: 700, color: C.gris,
                    textDecoration: 'underline', fontFamily: DS.fontBody }}>
                  Jour suivant →
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  )
}

function moisCourt(iso) {
  const d = new Date(`${iso}T12:00:00`)
  if (Number.isNaN(d.getTime())) return ''
  return new Intl.DateTimeFormat('fr-FR', { month: 'long' }).format(d)
}

// ── UNE CARTE DE LA JOURNÉE — l'état PAR LA COULEUR (maquette 2026-08-19) ───

function LigneJour({ ligne, ouvert, onBasculer, infoDe, logementNom, action = null, rondControle = null }) {
  const inter = ligne.type === 'intervention'
  const agent = ligne.agent
  const nom = inter ? (ligne.intervention.libelle || 'Intervention') : logementNom(ligne.run.logement_id)
  const info = inter ? null : infoDe(ligne.run.logement_id)
  const plage = inter ? null : libellePlage(info)
  const prenom = agent ? libelleDepuisEmail(agent) : null
  const valide = !inter && ligne.run.status === 'completed'
  const duree = inter
    ? (Number(ligne.intervention.duree_min) > 0 ? Number(ligne.intervention.duree_min) : null)
    : dureeDuMenage(ligne.run, { menage_duree_min: info?.dureeMenage ?? null })

  // Les TROIS états de carte : à faire / validé / sans agent.
  const etat = inter ? 'intervention' : valide ? 'valide' : agent ? 'a-faire' : 'sans-agent'
  const teintes = {
    'a-faire': { fond: '#fff', bord: C.bord },
    valide: { fond: C.vertPale, bord: C.vertBord },
    'sans-agent': { fond: C.peau, bord: C.terraBord },
    intervention: { fond: '#fff', bord: C.bord },
  }[etat]
  const heureValide = valide ? heureValidation(ligne.run.completed_at) : null

  return (
    <button type="button" data-testid={`mois-ligne-${ligne.id}`} data-etat={etat}
      onClick={inter ? undefined : onBasculer}
      style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10,
        padding: '10px 12px', background: teintes.fond,
        border: `1px solid ${teintes.bord}`, borderRadius: 11, textAlign: 'left',
        cursor: inter ? 'default' : 'pointer', fontFamily: DS.fontBody }}>
      {/* La pastille dit QUI — ou, à défaut, qu'il n'y a personne. */}
      {inter ? (
        <span aria-hidden="true" style={{ width: 28, height: 28, borderRadius: '50%',
          background: C.inter, flexShrink: 0 }} />
      ) : agent ? (
        <span aria-hidden="true" style={{ width: 28, height: 28, borderRadius: '50%',
          flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 9.5, fontWeight: 700,
          background: valide ? C.vert : C.cielVif, color: '#fff' }}>
          {String(initialsFromEmail(agent) || '??').toUpperCase().slice(0, 2)}
        </span>
      ) : (
        <span aria-hidden="true" style={{ width: 28, height: 28, borderRadius: '50%',
          flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          background: '#fff', border: `1.5px dashed ${C.terra}`, color: C.terra }}>
          <UserRoundSearch size={14} strokeWidth={2.2} />
        </span>
      )}

      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 12.5, fontWeight: 700, color: C.fonce,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{nom}</span>
        <span style={{ display: 'block', fontSize: 11, marginTop: 1,
          color: !inter && !agent ? C.terraSombre : C.texteDoux,
          fontWeight: !inter && !agent ? 700 : 400 }}>
          {inter
            ? `${prenom || 'sans agent'}`
            : !agent
              ? `sans agent${plage ? ` · ${plage}` : ''}`
              : valide
                ? `${prenom}${heureValide ? ` · validé à ${heureValide}` : ' · validé'}`
                : `${prenom}${plage ? ` · ${plage}` : ''}`}
        </span>
      </span>

      {/* La durée en pastille ciel, puis la coche des validés, puis le chevron. */}
      {duree != null && (
        <span data-testid={`mois-ligne-duree-${ligne.id}`}
          style={{ flexShrink: 0, fontSize: 10.5, fontWeight: 700, color: C.gris,
            background: '#e8f2fa', borderRadius: 8, padding: '3px 8px' }}>
          {formaterDuree(duree)}
        </span>
      )}
      {valide && (
        <Check size={16} strokeWidth={2.6} color={C.vert} style={{ flexShrink: 0 }} aria-hidden="true" />
      )}
      {/* LE ROND DE CONTRÔLE (point 5, Dimitri 2026-08-22) : le ménage est
          validé — mais a-t-il été CONTRÔLÉ ? Deux états, un coup d'œil. */}
      {rondControle}

      {/* MODE FILTRE : le bouton d'action de la ligne — « Assigner » (terracotta,
          il faut décider) ou « Remplir » (navy, il faut renseigner). stopPropagation :
          le clic du bouton ne doit pas AUSSI basculer la ligne. */}
      {action && (
        <span role="button" tabIndex={0} data-testid={action.testId}
          className="fbz-press fbz-cible-tactile"
          onClick={(e) => { e.stopPropagation(); action.onClic() }}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); action.onClic() } }}
          style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center',
            background: action.libelle === 'Assigner' ? C.terra : C.gris,
            color: '#fff', borderRadius: 8, padding: '6px 11px', fontSize: 11,
            fontWeight: 700, fontFamily: DS.fontBody, cursor: 'pointer' }}>
          {action.libelle}
        </span>
      )}
      {!inter && (ouvert
        ? <ChevronDown size={15} strokeWidth={2.2} color={C.muet} style={{ flexShrink: 0 }} />
        : <ChevronRight size={15} strokeWidth={2.2} color={C.muet} style={{ flexShrink: 0 }} />)}
    </button>
  )
}

// ── LE ROND DE CONTRÔLE D'UNE LIGNE (point 5, Dimitri 2026-08-22) ────────────
// Un ménage validé n'est pas forcément CONTRÔLÉ : deux états, un coup d'œil.
// MÊME CODE COULEUR QUE LA FRISE — les états d'un contrôle EXISTANT viennent
// de ETATS_PASTILLE (la pastille œil partagée), à l'hex près. S'y ajoutent
// trois états « avant contrôle » dans la même forme :
//   · pas encore contrôlé (désigné, fenêtre ouverte) — terracotta pâle ;
//   · personne de désigné — pointillés ambre (le langage de « Désigner ») ;
//   · hors fenêtre — gris éteint, la RAISON écrite en infobulle.
// ⚠️ span role=button, pas <button> : la ligne entière est déjà un bouton —
// un bouton imbriqué est invalide (même choix que le bouton « Assigner »).
function RondControleLigne({ run, controle, designe, heureContrat, onControler, onOuvrirControle }) {
  const statutConnu = controle ? ETATS_PASTILLE[controle.statut] : null
  let visuel, surClic
  if (statutConnu) {
    visuel = { fond: statutConnu.fond, bord: `1.5px solid ${statutConnu.lisere}`,
      contenu: statutConnu.texte, glyphe: statutConnu.glyphe, libelle: statutConnu.libelle }
    surClic = onOuvrirControle ? () => onOuvrirControle(controle) : null
  } else if (!dansFenetreControle(run.scheduled_date, heureContrat)) {
    visuel = { fond: '#f4f6f8', bord: '1.5px solid #d5e3ee', contenu: '#98a2ad',
      glyphe: null, libelle: raisonFenetrePassee(run.scheduled_date, heureContrat) }
    surClic = null
  } else if (!designe) {
    visuel = { fond: '#fff', bord: '1.5px dashed #d4a24c', contenu: '#8a5f19',
      glyphe: null, libelle: 'Pas encore contrôlé — personne de désigné' }
    surClic = onControler ? () => onControler(run) : null
  } else {
    visuel = { fond: '#fdf4f0', bord: '1.5px solid #b8412b', contenu: '#b8412b',
      glyphe: null, libelle: 'Pas encore contrôlé' }
    surClic = onControler ? () => onControler(run) : null
  }
  return (
    <span role={surClic ? 'button' : undefined} tabIndex={surClic ? 0 : undefined}
      data-testid={`mois-rond-controle-${run.id}`}
      className={surClic ? 'fbz-press fbz-hitbox44' : undefined}
      aria-label={visuel.libelle} title={visuel.libelle}
      onClick={surClic ? (e) => { e.stopPropagation(); surClic() } : undefined}
      onKeyDown={surClic ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); surClic() } } : undefined}
      onPointerDown={(e) => e.stopPropagation()}
      style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 2,
        height: 18, padding: '0 5px', borderRadius: 9, boxSizing: 'border-box',
        background: visuel.fond, border: visuel.bord, color: visuel.contenu,
        fontSize: 10, fontWeight: 800, lineHeight: 1,
        cursor: surClic ? 'pointer' : 'default' }}>
      <Eye size={10} strokeWidth={2.4} aria-hidden="true" />
      {visuel.glyphe === 'coche' && <Check size={10} strokeWidth={3} aria-hidden="true" />}
      {visuel.glyphe === 'point' && <span aria-hidden="true">!</span>}
      {visuel.glyphe === 'boucle' && <RotateCw size={10} strokeWidth={2.6} aria-hidden="true" />}
    </span>
  )
}

// ── LE TITRE D'UN BLOC — carré plein 26 px, icône blanche 15 (charte). ──────
function TitreBloc({ icone: Icone, fond, children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 9.5,
      fontWeight: 700, color: C.navy, textTransform: 'uppercase',
      letterSpacing: '.09em', fontFamily: DS.fontBody }}>
      <span aria-hidden="true" style={{ width: 26, height: 26, borderRadius: 8,
        background: fond, display: 'inline-flex', alignItems: 'center',
        justifyContent: 'center', flexShrink: 0 }}>
        <Icone size={15} strokeWidth={2.1} color="#fff" />
      </span>
      {children}
    </div>
  )
}

// ── LA CARTE D'UN MÉNAGE — MÊME LANGAGE QUE LE PANNEAU DE LA FRISE ──────────

function CarteMenage({
  run, info, nom, dayIso, blocsDuJour, assignOptions, regle, guestTimes, currentUser,
  controle = null,
  onReassign, onOpenChecklist, onSignalIncident, onOuvrirDuree, onAllerFrise,
}) {
  const bloc = blocsDuJour.find((b) => b.id === run.id) || null
  const duree = dureeDuMenage(run, { menage_duree_min: info?.dureeMenage ?? null })
  const plage = libellePlage(info)

  // ⚠️ QUI EST LIBRE — les agents complets restent VISIBLES, grisés. Les masquer
  // ferait croire qu'ils n'existent pas, et on les chercherait.
  const dispo = useMemo(() => {
    if (!bloc) return assignOptions.map((a) => ({ email: a.value, libre: true }))
    return agentsDisponibles(blocsDuJour.filter((b) => b.id !== run.id),
      assignOptions.map((a) => a.value), { debut: bloc.debut, fin: bloc.fin })
  }, [bloc, blocsDuJour, assignOptions, run.id])

  const arrivee = arriveeAffichee({
    arriveeFormulaire: guestTimes?.[run.id]?.arrival || null,
    checkinLogement: info?.checkin || null,
  })

  const nomAgent = (e) => {
    const o = assignOptions.find((x) => x.value === e)
    return o ? o.label : libelleDepuisEmail(e)
  }
  const prenomAssigne = run.assigned_provider_email
    ? nomAgent(run.assigned_provider_email) : null

  // La pastille d'état du bandeau — LES RÈGLES HABITUELLES (les mêmes hex que
  // le panneau) : la couleur pour les états qui veulent dire quelque chose,
  // le neutre translucide pour l'attente.
  // GO 2026-08-19 : les contrôles arrivent au Mois — « À reprendre » PRIME
  // (terracotta, les couleurs habituelles). Sinon l'état du ménage.
  const controleAttend = controle
    && (controle.statut === 'a_reprendre' || controle.statut === 'reprise_en_cours')
  const pastille = controleAttend
    ? { texte: 'À reprendre', fond: '#f0c4b6', bord: '#b8412b', couleur: '#7d2917' }
    : run.status === 'completed'
      ? { texte: 'Validé', fond: C.vert, bord: '#1a4f3a', couleur: C.vertSombre }
      : run.status === 'in_progress'
        ? { texte: 'En cours', fond: C.ciel, bord: C.navy, couleur: C.cielTexte }
        : { texte: 'À venir', fond: 'rgba(255,255,255,.14)', bord: 'rgba(255,255,255,.24)', couleur: '#fff' }

  const dBandeau = new Date(`${dayIso}T12:00:00`)
  const dateBandeau = Number.isNaN(dBandeau.getTime()) ? (dayIso || '—')
    : new Intl.DateTimeFormat('fr-FR', {
        weekday: 'long', day: 'numeric', month: 'long',
      }).format(dBandeau)

  return (
    <div data-testid={`mois-carte-${run.id}`}
      style={{ margin: '8px 0 4px', border: `1px solid ${C.bord}`, borderRadius: 12,
        overflow: 'hidden', background: '#fff',
        boxShadow: '0 8px 26px rgba(35,50,64,.12)' }}>

      {/* ══ LE BANDEAU NAVY — deux écrans, une seule identité. ══ */}
      <div data-testid={`mois-carte-bandeau-${run.id}`}
        style={{ position: 'relative', overflow: 'hidden', background: NAVY_DEGRADE,
          padding: '13px 14px' }}>
        <span aria-hidden="true" style={{ position: 'absolute', top: -28, right: -18,
          width: 84, height: 84, borderRadius: '50%', background: 'rgba(255,255,255,.05)' }} />
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: 16, fontWeight: 700, color: '#fff',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{nom}</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5,
              color: 'rgba(255,255,255,.78)', marginTop: 2 }}>
              <CalendarDays size={12} strokeWidth={2} style={{ flexShrink: 0 }} />
              {dateBandeau}
            </span>
          </span>
          <span data-testid={`mois-carte-pastille-${run.id}`}
            style={{ flexShrink: 0, fontSize: 10.5, fontWeight: 700,
              background: pastille.fond, border: `1px solid ${pastille.bord}`,
              color: pastille.couleur, borderRadius: 20, padding: '4px 10px' }}>
            {pastille.texte}
          </span>
        </div>
        {/* L'ARRIVÉE DU VOYAGEUR — carte translucide : c'est ce qui borne le
            ménage. Une donnée absente SE DIT. */}
        <div data-testid={`mois-carte-arrivee-${run.id}`}
          style={{ position: 'relative', marginTop: 10, background: 'rgba(255,255,255,.13)',
            borderRadius: 10, padding: '7px 10px', maxWidth: 240 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 9,
            fontWeight: 700, color: 'rgba(255,255,255,.62)', textTransform: 'uppercase',
            letterSpacing: '.08em' }}>
            <Clock size={11} strokeWidth={2.2} /> Arrivée
          </span>
          <span style={{ display: 'block', fontSize: 12.5, fontWeight: 700, color: '#fff', marginTop: 1 }}>
            {[
              Number(run.guest_count) > 0 ? `${run.guest_count} pers.` : null,
              arrivee ? formaterHeureCourte(arrivee.minutes) : 'heure inconnue',
            ].filter(Boolean).join(' · ')}
            {arrivee?.source === 'formulaire' ? ' · annoncé' : ''}
          </span>
        </div>
      </div>

      {/* ══ BLOC 1 · QUI FAIT CE MÉNAGE — fond #f4f9fd, icône sur #5c8ab8. ══ */}
      <div style={{ background: '#f4f9fd', padding: '12px 13px',
        display: 'flex', flexDirection: 'column', gap: 8 }}>
        <TitreBloc icone={User} fond={C.cielVif}>Qui fait ce ménage</TitreBloc>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {dispo.map(({ email, libre }) => {
            const actuel = email === run.assigned_provider_email
            return (
              // ⚠️ RÈGLE DE LA FRISE (alignement 2026-08-22) : un agent occupé
              // reste CLIQUABLE — « complet » informe, il n'interdit pas. Le
              // conflit créé s'affichera, comme partout. Avant, les trois
              // boutons pouvaient être tous morts (« rien à toucher »).
              <button key={email} type="button"
                data-testid={`mois-assigner-${run.id}-${email}`}
                onClick={() => !actuel && onReassign(run, email)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 5, borderRadius: 7,
                  padding: '5px 9px', fontSize: 11, fontWeight: 700, fontFamily: DS.fontBody,
                  border: actuel ? `2px solid ${C.navy}` : `1px solid ${C.bord}`,
                  background: actuel ? C.cielPale : '#fff',
                  color: actuel ? C.cielTexte : (libre ? C.gris : C.texteDoux),
                  cursor: actuel ? 'default' : 'pointer' }}>
                {String(initialsFromEmail(email) || '??').toUpperCase().slice(0, 2)} · {nomAgent(email)}
                {!libre && !actuel ? ' — complet' : ''}
              </button>
            )
          })}
        </div>
        {/* ⚠️ ON RAPPELLE LE RATTACHEMENT PERMANENT, on ne le crée pas ici : il
            vit dans les règles d'assignation. Sans ce rappel, on assigne
            quelqu'un d'autre sans savoir qu'une règle existait. */}
        {regle?.provider_email && (
          <div data-testid={`mois-regle-${run.id}`}
            style={{ background: '#fff', border: `1px solid ${C.bord}`, borderRadius: 9,
              padding: '8px 11px', fontSize: 11, color: C.texteDoux, lineHeight: 1.5 }}>
            {run.assigned_provider_email
              ? (<>Ce logement est rattaché à <strong style={{ color: C.gris }}>{nomAgent(regle.provider_email)}</strong> en permanence.</>)
              // ⚠️ UNE IDÉE PAR PHRASE (Dimitri 2026-08-22) : le constat, puis l'habitude.
              : (<>Personne sur ce ménage. <strong style={{ color: C.gris }}>{nomAgent(regle.provider_email)}</strong> s'en occupe d'habitude.</>)}
          </div>
        )}
      </div>

      {/* ══ BLOC 2 · QUAND — fond blanc, icône sur #87b8e6, un VRAI bloc. ══ */}
      <div style={{ background: '#fff', padding: '12px 13px',
        display: 'flex', flexDirection: 'column', gap: 8 }}>
        <TitreBloc icone={Clock} fond={C.ciel}>Quand</TitreBloc>
        <div data-testid={`mois-quand-${run.id}`}
          style={{ display: 'flex', alignItems: 'center', gap: 10,
            background: 'linear-gradient(100deg, #e8f2fa 0%, #f4f9fd 100%)',
            borderRadius: 10, padding: '10px 12px' }}>
          <span aria-hidden="true" style={{ width: 32, height: 32, borderRadius: 9,
            background: C.cielVif, flexShrink: 0, display: 'inline-flex',
            alignItems: 'center', justifyContent: 'center' }}>
            <Hourglass size={16} strokeWidth={2.1} color="#fff" />
          </span>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: 9.5, fontWeight: 700,
              letterSpacing: '.08em', textTransform: 'uppercase', color: C.gris }}>
              {plage ? `Entre ${plage.replace(' → ', ' et ')}` : 'Plage inconnue'}
            </span>
            <span style={{ display: 'block', fontSize: 15, fontWeight: 700, color: C.cielTexte }}>
              {formaterDuree(duree)} de ménage
            </span>
          </span>
          <button type="button" data-testid={`mois-duree-${run.id}`}
            onClick={() => onOuvrirDuree(bloc || { id: run.id, nom, dureeMin: duree, logementId: run.logement_id })}
            className="fbz-press"
            style={{ flexShrink: 0, background: '#fff', border: `1px solid ${C.bord}`,
              borderRadius: 8, padding: '7px 12px', fontSize: 11, fontWeight: 700,
              color: C.gris, cursor: 'pointer', fontFamily: DS.fontBody }}>
            Changer
          </button>
        </div>
        {/* ⚠️ ON NE FIXE PAS D'HEURE ICI. On dit ce qu'il en est, et où le
            faire — AVEC LE PRÉNOM de l'agent, pas « l'agent ». */}
        <div data-testid={`mois-heure-mention-${run.id}`}
          style={{ border: `1.5px dashed ${C.bord}`, borderRadius: 9,
            padding: '7px 11px', fontSize: 11, color: C.texteDoux }}>
          {run.heure_fixee
            ? `Heure fixée à ${formaterHeureCourte(minutesDeHeure(String(run.heure_fixee).slice(0, 5)))} — modifiable dans la frise`
            : `Aucune heure fixée — ${prenomAssigne || "l'agent assigné"} choisit dans la plage.`}
        </div>
      </div>

      {/* ══ BLOC 3 · SUR PLACE — fond #f2f7fb, icône sur #3d5166. ══ */}
      <div style={{ background: '#f2f7fb', padding: '12px 13px',
        display: 'flex', flexDirection: 'column', gap: 8 }}>
        <TitreBloc icone={MapPin} fond={C.navy}>Sur place</TitreBloc>
        {info?.boiteCles && <CodeNavy code={info.boiteCles} runId={run.id} />}
        {info?.adresse && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 9,
            background: '#fff', border: `1px solid ${C.bord}`, borderRadius: 10,
            padding: '9px 11px' }}>
            <span data-testid={`mois-adresse-${run.id}`}
              style={{ flex: 1, minWidth: 0, fontSize: 11.5, color: C.gris, lineHeight: 1.45 }}>
              {info.adresse}
            </span>
            <a data-testid={`mois-itineraire-${run.id}`}
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(info.adresse)}`}
              target="_blank" rel="noopener noreferrer" aria-label="Itinéraire"
              title="Itinéraire" className="fbz-press"
              style={{ flexShrink: 0, width: 40, height: 40, borderRadius: 10,
                background: C.cielPale, display: 'inline-flex', alignItems: 'center',
                justifyContent: 'center' }}>
              <MapPin size={17} strokeWidth={2.1} color={C.navy} />
            </a>
          </div>
        )}
      </div>

      {/* ══ LES ACTIONS EN PIED — filet, fond blanc. ══ */}
      <div style={{ background: '#fff', borderTop: `1px solid ${C.contour}`,
        padding: '11px 13px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ display: 'flex', gap: 7 }}>
          <button type="button" data-testid={`mois-checklist-${run.id}`}
            onClick={() => onOpenChecklist(run)} className="fbz-press"
            style={{ flex: 1, display: 'inline-flex', alignItems: 'center',
              justifyContent: 'center', gap: 6, minHeight: 40, background: '#fff',
              border: `1px solid ${C.cielVif}`, borderRadius: 9, color: C.navy,
              fontSize: 11.5, fontWeight: 700, cursor: 'pointer', fontFamily: DS.fontBody }}>
            <ClipboardList size={14} strokeWidth={2} /> Checklist
          </button>
          <button type="button" data-testid={`mois-incident-${run.id}`}
            onClick={() => onSignalIncident(run)} className="fbz-press"
            style={{ flex: 1, display: 'inline-flex', alignItems: 'center',
              justifyContent: 'center', gap: 6, minHeight: 40, background: '#fff',
              border: `1px solid ${C.terraBord}`, borderRadius: 9, color: C.terraSombre,
              fontSize: 11.5, fontWeight: 700, cursor: 'pointer', fontFamily: DS.fontBody }}>
            <AlertTriangle size={14} strokeWidth={2} /> Signaler
          </button>
        </div>
        <button type="button" data-testid={`mois-frise-${run.id}`}
          onClick={onAllerFrise} className="fbz-press"
          style={{ width: '100%', display: 'inline-flex', alignItems: 'center',
            justifyContent: 'center', gap: 7, minHeight: 42, background: NAVY_DEGRADE,
            border: 'none', borderRadius: 9, color: '#fff', fontSize: 12,
            fontWeight: 700, cursor: 'pointer', fontFamily: DS.fontBody }}>
          <CalendarRange size={15} strokeWidth={2} /> Voir dans la frise
        </button>

        {/* ⚠️ LA TRACE « DERNIÈRE MAIN » (ajout 2026-08-14) — la même que la
            frise, en une ligne sobre. `service_role` = une écriture serveur
            (cron, cascade) : on dit « automatiquement », jamais un faux nom. */}
        {run.modifie_par && (
          <div data-testid={`mois-trace-${run.id}`}
            style={{ fontSize: 10.5, color: C.muet }}>
            Modifié {run.modifie_par === 'service_role'
              ? 'automatiquement'
              : `par ${firstNameFromEmail(run.modifie_par) || libelleDepuisEmail(run.modifie_par)}`}
            {jourLisible(run.modifie_le) ? ` ${jourLisible(run.modifie_le)}` : ''}
          </div>
        )}
      </div>
    </div>
  )
}

/** « mardi 12 août » — le jour de la dernière main, lisible sans année. */
function jourLisible(ts) {
  if (!ts) return null
  const d = new Date(ts)
  if (Number.isNaN(d.getTime())) return null
  return new Intl.DateTimeFormat('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' }).format(d)
}

/** LE CODE D'ENTRÉE EN CARTE NAVY PLEINE — comme partout ailleurs (charte). */
function CodeNavy({ code, runId }) {
  const [copie, setCopie] = useState(false)
  return (
    <div data-testid={`mois-code-${runId}`}
      style={{ position: 'relative', overflow: 'hidden', background: NAVY_DEGRADE,
        borderRadius: 11, padding: '10px 12px', display: 'flex',
        alignItems: 'center', gap: 10 }}>
      <span aria-hidden="true" style={{ width: 36, height: 36, borderRadius: 10,
        background: 'rgba(255,255,255,.14)', flexShrink: 0, display: 'inline-flex',
        alignItems: 'center', justifyContent: 'center' }}>
        <Key size={17} strokeWidth={2.1} color="#fff" />
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 9, fontWeight: 700,
          letterSpacing: '.09em', textTransform: 'uppercase',
          color: 'rgba(255,255,255,.62)' }}>Boîte à clés</span>
        <span style={{ display: 'block', fontSize: 20, fontWeight: 700, color: '#fff',
          letterSpacing: '.14em', fontFamily: DS.fontDisplay }}>{code}</span>
      </span>
      <button type="button" data-testid={`mois-code-copier-${runId}`}
        onClick={() => {
          // Même garde que BarreActionsBloc/PanneauMenage : clipboard absent
          // (http, vieux navigateur) → pas de crash, le code reste lisible.
          const p = navigator.clipboard?.writeText?.(code)
          if (p && typeof p.then === 'function') {
            p.then(() => {
              setCopie(true)
              setTimeout(() => setCopie(false), 1600)
            }).catch(() => { /* presse-papier refusé : le code reste lisible à l'écran */ })
          }
        }}
        className="fbz-press"
        style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 5,
          background: 'rgba(255,255,255,.14)', border: '1px solid rgba(255,255,255,.24)',
          borderRadius: 8, padding: '6px 10px', color: '#fff', fontSize: 10.5,
          fontWeight: 700, cursor: 'pointer', fontFamily: DS.fontBody }}>
        <Copy size={12} strokeWidth={2.1} /> {copie ? 'Copié' : 'Copier'}
      </button>
    </div>
  )
}
